import * as React from "react";
import { useState } from "react";
import {
  createStep1Schema,
  validateStep,
} from "../../../validation/abstractSchema";
import { toastMultilineErrors } from "../../../util";

interface AbstractCategory {
  id: number;
  name: string;
}

interface AbstractMode {
  id: number;
  name: string;
}

interface AbstractTopic {
  id: number;
  name: string;
}

interface TypeMapping {
  abstractCategory?: AbstractCategory;
  abstractMode?: AbstractMode;
  abstractTopic?: AbstractTopic;
}

interface Step1Props {
  typeMappings: TypeMapping[];
  formData: any;
  setFormData: (data: any) => void;
  setCurrentStep: (step: number) => void;
  currentStep: number;
  submissionMode: string;
  onNext: () => void;
  onBack: () => void;
}

const Step1: React.FC<Step1Props> = ({
  typeMappings,
  formData,
  setFormData,
  submissionMode,
  onNext,
  onBack,
}) => {
  /*******************************
   * State variables
   *******************************/
  const [selectedMode, setSelectedMode] = useState(formData.mode || "");
  const [selectedCategory, setSelectedCategory] = useState(
    formData.category || "",
  );
  const [selectedTopic, setSelectedTopic] = useState(formData.topic || "");
  const [posterAward, setPosterAward] = useState(formData.posterAward || {});

  // Get unique modes
  const modes = [
    ...new Map(
      typeMappings
        .map((m): [number, AbstractMode] => [
          m.abstractMode?.id || 0,
          m.abstractMode!,
        ])
        .filter(([id, mode]) => id && mode),
    ).values(),
  ] as AbstractMode[];

  // Get categories based on selected mode
  const availableCategories =
    selectedMode !== ""
      ? ([
          ...new Map(
            typeMappings
              .filter((m) => m.abstractMode?.id === parseInt(selectedMode))
              .map((m): [number, AbstractCategory] => [
                m.abstractCategory?.id || 0,
                m.abstractCategory!,
              ])
              .filter(([id, cat]) => id && cat),
          ).values(),
        ] as AbstractCategory[])
      : [];

  // Get topics based on selected mode and category
  const availableTopics =
    selectedMode !== "" && selectedCategory !== ""
      ? ([
          ...new Map(
            typeMappings
              .filter(
                (m) =>
                  m.abstractMode?.id === parseInt(selectedMode) &&
                  m.abstractCategory?.id === parseInt(selectedCategory),
              )
              .map((m): [number, AbstractTopic] => [
                m.abstractTopic?.id || 0,
                m.abstractTopic!,
              ])
              .filter(([id, topic]) => id && topic),
          ).values(),
        ] as AbstractTopic[])
      : [];

  /*******************************
   * Handlers
   *******************************/
  const handleModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedMode(value);
    setSelectedCategory("");
    setSelectedTopic("");
    setPosterAward({});
    setFormData({
      ...formData,
      mode: value,
      category: "",
      topic: "",
      posterAward: {},
    });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedCategory(value);
    setSelectedTopic("");
    setFormData({ ...formData, category: value, topic: "" });
  };

  const handleTopicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedTopic(value);
    setFormData({ ...formData, topic: value });
  };

  const handleNext = () => {
    const stepData = {
      abstract_mode_id: selectedMode,
      abstract_category_id: selectedCategory,
      abstract_topic_id: selectedTopic,
      posterAward: {
        paParticipation: posterAward.paParticipation || false,
        paAge: posterAward.paAge || "",
        paProofOfIdentity: posterAward.paProofOfIdentity || null,
      },
    };

    const validation = validateStep(stepData, createStep1Schema());
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors || {});
      toastMultilineErrors(errorMessages);
      return;
    }

    setFormData({
      ...formData,
      abstract_mode_id: selectedMode,
      abstract_category_id: selectedCategory,
      abstract_topic_id: selectedTopic,
      posterAward: posterAward,
    });
    onNext();
  };

  return (
    <div className="relative w-full bg-white">
      <div className="flex flex-col items-start w-full space-y-10">
        {/* Abstract Type Section */}
        <div className="flex flex-col w-full space-y-2">
          <p className="text-lg font-bold leading-8 text-gray-900">
            Abstract Type
          </p>
          <p className="text-md font-normal leading-7 text-gray-900">
            Please fill up the abstract type information accordingly
          </p>
        </div>

        {/* Form Fields */}
        <div className="flex flex-col w-full space-y-6">
          {/* First Row: Mode and Category */}
          <div className="flex w-full space-x-10">
            {/* Select Presentation Mode */}
            <div className="flex flex-col w-full space-y-1 md:w-1/2">
              <label className="text-sm font-normal leading-5 text-gray-500">
                Select Presentation Mode
              </label>
              <select
                value={selectedMode}
                onChange={handleModeChange}
                className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Options</option>
                {modes.map((mode) => (
                  <option key={mode.id} value={mode.id}>
                    {mode.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Select Abstract Category - Only show when mode is selected */}
            {selectedMode && (
              <div className="flex flex-col w-full space-y-1 md:w-1/2">
                <label className="text-sm font-normal leading-5 text-gray-500">
                  Select Abstract Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Options</option>
                  {availableCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Second Row: Topic - Only show when category is selected */}
          {selectedCategory && (
            <div className="flex flex-col w-full pr-0 space-y-1 md:w-1/2 md:pr-5">
              <label className="text-sm font-normal leading-5 text-gray-500">
                Select Abstract Topic
              </label>
              <select
                value={selectedTopic}
                onChange={handleTopicChange}
                className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Options</option>
                {availableTopics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* IUPAC Poster Award Participation */}
        {/* {selectedMode && selectedMode === "2" && selectedTopic && (
          <div className="flex flex-col w-full pr-0 space-y-1 md:w-1/2 md:pr-5">
            <p className="text-lg font-bold leading-8 text-gray-900">
              IUPAC Poster Award
            </p>
            <label
              htmlFor="paParticipation"
              className="text-sm font-normal leading-5 text-gray-700"
            >
              Do you want to participate in the IUPAC Poster Award?{" "}
              <a
                href="https://msn2026.com"
                target="_blank"
                rel="noopener noreferrer"
                className="italic text-blue-600"
              >
                Read more
              </a>
            </label>
            <p className="text-sm font-normal leading-5 text-gray-500">
              *This award is open to the MSN registered participants only.
            </p>
            <div className="flex items-center mb-4 space-x-2">
              <input
                type="checkbox"
                id="paParticipation"
                checked={posterAward.paParticipation || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setPosterAward({
                    paParticipation: isChecked,
                    paAge: null,
                    paProofOfIdentity: null,
                  });
                  setFormData({
                    ...formData,
                    posterAward: {
                      paParticipation: isChecked,
                      paAge: null,
                      paProofOfIdentity: null,
                    },
                  });
                }}
                className="w-4 h-4"
                style={{ accentColor: "red" }}
              />
              <label
                htmlFor="paParticipation"
                className="text-sm font-normal leading-5 text-red-700 cursor-pointer"
              >
                IUPAC Poster Award
              </label>
            </div>
            {posterAward.paParticipation && (
              <div className="flex flex-col space-y-4">
                <div className="flex flex-col space-y-1">
                  <label
                    htmlFor="paAge"
                    className="text-sm font-normal leading-5 text-gray-500"
                  >
                    Age
                  </label>
                  <input
                    type="number"
                    id="paAge"
                    value={posterAward.paAge || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPosterAward({ ...posterAward, paAge: value });
                      setFormData({
                        ...formData,
                        posterAward: { ...posterAward, paAge: value },
                      });
                    }}
                    className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <label
                    htmlFor="paProofOfIdentity"
                    className="text-sm font-normal leading-5 text-gray-500"
                  >
                    Upload Proof of Identity
                  </label>
                  <input
                    type="file"
                    id="paProofOfIdentity"
                    accept=".pdf,.jpg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setPosterAward({
                        ...posterAward,
                        paProofOfIdentity: file,
                      });
                      setFormData({
                        ...formData,
                        posterAward: {
                          ...posterAward,
                          paProofOfIdentity: file,
                        },
                      });
                    }}
                    className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
        )} */}

        {/* Action Buttons */}
        <div className="flex items-center justify-between w-full">
          <button
            onClick={onBack}
            className="flex items-center px-6 py-2 transition-colors bg-blue-100 rounded cursor-pointer hover:bg-blue-200"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm font-normal leading-5 text-gray-900">
                ← Back to Dashboard
              </span>
            </div>
          </button>

          <button
            onClick={handleNext}
            className="flex items-center px-6 py-2 transition-colors bg-blue-900 rounded cursor-pointer hover:bg-blue-800"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm font-normal leading-5 text-white">
                Author / Co-authors →
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step1;
